import { Tag } from "@carbon/react";
import { NullableTableColumns } from "@flaminem/ui-components";
import { sortBy, toDate } from "@flaminem/utils";

import { AlertDecisionTag } from "src/components/Alerts/AlertDecisionTag";
import { AlertStatusTag } from "src/components/Alerts/AlertStatusTag";
import { useFormatDate, useT } from "src/internationalization";
import { AlertStatus, AlertSummary, ShortAlert } from "src/types/alert";
import { ScenarioExecutionResultWithCase } from "src/types/scenario";

type UseAlertsAndExecutionsTabDataProps = {
  executionResults?: ScenarioExecutionResultWithCase[];
  alerts?: (ShortAlert | AlertSummary)[];
  selectedCase?: {
    id: string;
    request: string | null;
  };
  selectedAlert?: {
    id: string;
  };
};

export type AlertsAndExecutions = {
  id: string;
  executedOn: Date;
  scenario: string | undefined;
  alert: React.ReactNode;
  score: React.ReactNode;
  decision: React.ReactNode;
  status: AlertStatus | null;
};
export const useAlertsAndExecutionsTabData = ({
  executionResults,
  alerts,
  selectedCase,
  selectedAlert,
}: UseAlertsAndExecutionsTabDataProps) => {
  const t = useT();
  const formatDate = useFormatDate();

  const executions: AlertsAndExecutions[] | undefined = executionResults?.map((executionResult) => {
    const linkedAlert = executionResult.alert?.id
      ? alerts?.find((a) => a.id === executionResult.alert?.id)
      : undefined;
    return {
      id: executionResult.id,
      executedOn: executionResult.date,
      scenario: executionResult?.scenarioVersion?.scenario?.name,
      alert: executionResult.alert ? (
        selectedAlert && executionResult.alert.id === selectedAlert.id ? (
          <Tag>
            {t("page.case.tab.executions.result.alert.value", { number: selectedCase?.request })}
          </Tag>
        ) : (
          t("page.case.tab.executions.result.alert.value", { number: selectedCase?.request })
        )
      ) : null,
      score: <Tag type="magenta">{executionResult.globalScore}</Tag>,
      decision: executionResult.alert?.decision ? (
        <AlertDecisionTag decision={executionResult.alert?.decision} />
      ) : (
        <></>
      ),
      status: linkedAlert?.status ?? null,
    };
  });

  const manualAlerts: AlertsAndExecutions[] | undefined = alerts
    ?.filter((alert) => alert.origin === "manual")
    ?.map((alert) => ({
      id: alert.id,
      executedOn: alert.adminStatus.creation,
      scenario: "Manual",
      alert:
        alert.id === selectedAlert?.id ? (
          <Tag>
            {t("page.case.tab.executions.result.alert.value", { number: selectedCase?.request })}
          </Tag>
        ) : (
          t("page.case.tab.executions.result.alert.value", { number: selectedCase?.request })
        ),
      score: <></>,
      decision: alert.decision ? <AlertDecisionTag decision={alert.decision} /> : <></>,
      status: alert.status,
    }));

  const alertsAndExecutions =
    executions && manualAlerts
      ? [...executions, ...manualAlerts]
      : executions || manualAlerts || [];

  const alertsAndExecutionsSort = sortBy(alertsAndExecutions, "executedOn", "desc");

  const columns: NullableTableColumns<AlertsAndExecutions> = {
    executedOn: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("page.case.tab.executions.result.date"),
      render: (row) => {
        return formatDate(toDate(row.executedOn), {
          timeStyle: "short",
          dateStyle: "medium",
        });
      },
    },
    scenario: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("page.case.tab.executions.result.scenario"),
      render: (row) =>
        row.scenario === "Manual" ? t("caseVersions.diff.executions.manual.alert") : row.scenario,
    },
    status: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("page.case.tab.executions.result.status"),
      render: (row) => (row.status ? <AlertStatusTag status={row.status} /> : null),
    },
    alert: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("page.case.tab.alerts.and.executions.alert"),
      render: (row) => row.alert,
    },
    score: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("page.case.tab.executions.result.score"),
      render: (row) => row.score,
    },
    decision: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("page.case.tab.executions.result.decision"),
      render: (row) => row.decision,
    },
  };

  return {
    data: alertsAndExecutionsSort,
    columns,
  };
};
